// Raw API Response Types (matching actual API)
export interface MerchantCouponItemRawApiResponse {
    id: string;
    image_url: string;
    merchant_name: string;
    title: string;
    points_required: number;
    price: string;
    valid_from: string;
    valid_to: string;
    quantity: number;
    exchanged: number;
    remaining: number;
    status: string;
    is_active: boolean;
    local_merchant_id: string;
    deletable: boolean;
    editable: boolean;
}

export interface MerchantCouponListRawApiResponse {
    list: MerchantCouponItemRawApiResponse[];
    paging: {
        count: number;
        index: number;
        size: number;
        total: number;
    };
}

// Logic Model (API Request/Response)
export interface MerchantCouponListApiRequest {
    filter: MerchantCouponListFilter;
    pagination: {
        index: number;
        size: number;
    };
    sorting?: {
        field?: string;
        direction?: 'asc' | 'desc';
    };
}

export interface MerchantCouponListFilter {
    merchant_name?: string | null;
    status?: 'active' | 'inactive' | null;
    search_keyword?: string | null;
    points_required?: {
        min?: number;
        max?: number;
    } | null;
    quantity?: {
        min?: number;
        max?: number;
    } | null;
    exchanged?: {
        min?: number;
        max?: number;
    } | null;
    remaining?: {
        min?: number;
        max?: number;
    } | null;
    // Legacy fields for backward compatibility
    coupon_name?: string | null;
    use_per_customer?: number | null;
    used_quantity?: number | null;
    available_quantity?: number | null;
    /** Single value (legacy) or array when using dropdown filter (same as admin) */
    requested_status?: ('active' | 'low_stock' | 'out_of_stock' | 'closed') | ('active' | 'low_stock' | 'out_of_stock' | 'closed')[] | null;
    [key: string]: any;
}

export type MerchantCouponStatus = 'active' | 'inactive' | 'low_stock' | 'out_of_stock';

export interface MerchantCouponItemApi {
    id: string;
    image_url: string;
    title: string;
    description: string;
    code: string;
    points_required: number;
    valid_from: string | Date;
    valid_to: string | Date;
    is_active: boolean;
    quantity: number;
    created_at: string | Date;
    updated_at: string | Date;
    merchant_id: string;
    merchant_name: string;
    use_per_customer: number | null;
    used_quantity: number;
    available_quantity: number;
    requested_status: MerchantCouponStatus;
    status: string;
    deletable: boolean;
    editable: boolean;
}

// Raw API Response for Statistics
export interface MerchantCouponStatisticRawApiResponse {
    total_active_coupons: number;
    total_coupons: number;
    total_viewed_coupons: number;
    coupons_redeemed: number;
    usage_count: number;
    low_or_out_of_stock: number;
}

export interface MerchantRewardSummaryApi {
    total_active_coupons: number;
    total_coupons: number;
    total_viewed_coupons: number;
    coupons_redeemed: number;
    usage_count: number;
    low_or_out_of_stock: number;
}

// UI Model (State Management)
export interface MerchantCouponListUIState {
    id: string;
    imageUrl: string;
    title: string;
    description: string;
    merchantName: string;
    value: string;
    pointsRequired: number;
    validFrom: Date;
    validTo: Date;
    quantity: number;
    exchanged: number;
    remaining: number;
    remainingStatus: 'normal' | 'low_stock' | 'out_of_stock';
    status: MerchantCouponStatus;
    isActive: boolean;
    deletable: boolean;
    editable: boolean;
}

export interface MerchantCouponListPageState {
    coupons: MerchantCouponListUIState[];
    summary: {
        totalActiveCoupons: number;
        totalCoupons: number;
        totalViewedCoupons: number;
        couponsRedeemed: number;
        usageCount: number;
        lowOutOfStock: number;
    };
    filters: MerchantCouponListFilter;
    pagination: {
        index: number;
        size: number;
        count: number;
    };
    loading: {
        onGetList: boolean;
        onGetSummary: boolean;
    };
    error: string | null;
}

export const getMerchantCouponStatus = (
    validFrom: Date,
    validTo: Date,
    remaining: number,
    now: Date = new Date(),
): MerchantCouponStatus => {
    if (
        Number.isNaN(validFrom.getTime()) ||
        Number.isNaN(validTo.getTime()) ||
        now < validFrom ||
        now > validTo
    ) {
        return 'inactive';
    }

    if (remaining <= 0) {
        return 'out_of_stock';
    }

    if (remaining <= 1) {
        return 'low_stock';
    }

    return 'active';
};

// Mapper from Raw API Response to MerchantCouponItemApi
export const mapRawApiResponseToApiResponse = (rawData: MerchantCouponItemRawApiResponse): MerchantCouponItemApi => {
    const validFrom = new Date(rawData.valid_from);
    const validTo = new Date(rawData.valid_to);
    const remaining = rawData.remaining;
    const requestedStatus = getMerchantCouponStatus(validFrom, validTo, remaining);
    
    return {
        id: rawData.id,
        image_url: rawData.image_url || '',
        title: rawData.title || '',
        description: '', // Not provided in API response
        code: '', // Not provided in API response
        points_required: rawData.points_required || 0,
        valid_from: rawData.valid_from,
        valid_to: rawData.valid_to,
        is_active: rawData.is_active,
        quantity: rawData.quantity || 0,
        created_at: '', // Not provided in API response
        updated_at: '', // Not provided in API response
        merchant_id: rawData.local_merchant_id || '',
        merchant_name: rawData.merchant_name || '',
        use_per_customer: null, // Not provided in API response
        used_quantity: rawData.exchanged || 0,
        available_quantity: remaining,
        requested_status: requestedStatus,
        status: requestedStatus,
        deletable: rawData.deletable ?? true, // Default to true if not provided
        editable: rawData.editable ?? true, // Default to true if not provided
    };
};

// Data Mapper
export const mapApiResponseToUI = (apiData: MerchantCouponItemApi): MerchantCouponListUIState => {
    const validFrom = typeof apiData.valid_from === 'string' ? new Date(apiData.valid_from) : apiData.valid_from;
    const validTo = typeof apiData.valid_to === 'string' ? new Date(apiData.valid_to) : apiData.valid_to;
    
    // Calculate remaining
    const remaining = apiData.available_quantity ?? (apiData.quantity - apiData.used_quantity);
    
    // Determine remaining status
    let remainingStatus: 'normal' | 'low_stock' | 'out_of_stock' = 'normal';
    if (remaining === 0) {
        remainingStatus = 'out_of_stock';
    } else if (remaining <= 1) {
        remainingStatus = 'low_stock';
    }
    
    // Format value from price field if available, otherwise calculate from points
    let value: string;
    if (apiData.points_required) {
        const price = apiData.points_required / 100;
        value = `$${price} (${apiData.points_required} pts)`;
    } else {
        value = `0 pts`;
    }
    
    return {
        id: apiData.id,
        imageUrl: apiData.image_url,
        title: apiData.title,
        description: apiData.description,
        merchantName: apiData.merchant_name,
        value,
        pointsRequired: apiData.points_required,
        validFrom,
        validTo,
        quantity: apiData.quantity,
        exchanged: apiData.used_quantity,
        remaining,
        remainingStatus,
        status: getMerchantCouponStatus(validFrom, validTo, remaining),
        isActive: apiData.is_active,
        deletable: apiData.deletable ?? true, // Default to true if not provided
        editable: apiData.editable ?? true, // Default to true if not provided
    };
};

export const initialMerchantCouponListPageState: MerchantCouponListPageState = {
    coupons: [],
    summary: {
        totalActiveCoupons: 0,
        totalCoupons: 0,
        totalViewedCoupons: 0,
        couponsRedeemed: 0,
        usageCount: 0,
        lowOutOfStock: 0,
    },
    filters: {},
    pagination: {
        index: 1,
        size: 20,
        count: 0,
    },
    loading: {
        onGetList: false,
        onGetSummary: false,
    },
    error: null,
};
